import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { SubscriptionCreationResult } from 'src/app/model/subscription-creation-result';
import { DialogService } from 'src/app/service/dialog.service';
import { NavigationService } from 'src/app/service/navigation.service';
import { AppScanEventType, ScanCameraService } from 'src/app/service/scan-camera-service.service';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { AppScanResultMessage } from 'src/app/types/app.types';

@Component({
    selector: 'subscription-via-barcode',
    templateUrl: './subscription-via-barcode.component.html',
    styleUrls: ['./subscription-via-barcode.component.scss'],
    providers: [ScanCameraService],
})
export class SubscriptionViaBarcodeComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('videoBackground') videoBackground: ElementRef<HTMLVideoElement> | undefined;
    @ViewChild('cameraOutput') cameraOutput: ElementRef<HTMLDivElement> | undefined;

    processing: boolean = false;
    readonly initialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    permissionsError: boolean = false;
    message: AppScanResultMessage | null = null;
    private backgroundVideoStream: MediaStream | undefined;
    private requestId: string | null = null;
    private readonly dead$ = new Subject<void>();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private navigationService: NavigationService,
        private scanCameraService: ScanCameraService,
        private subscriptionService: SubscriptionService,
        private dialogService: DialogService
    ) {
        this.requestId = this.route.snapshot.queryParams['id'];
    }

    ngOnInit(): void {
        this.navigationService.activity.reset();
        this.navigationService.activity.toolbar.hide();
    }

    ngAfterViewInit(): void {
        this.initializeVideoStream();
    }

    ngOnDestroy(): void {
        this.dead$.next();
        this.dead$.complete();

        if (this.backgroundVideoStream) {
            this.backgroundVideoStream.getTracks().forEach((track) => track.stop());
        }
    }

    activateManualInput(): void {
        let params = { replaceUrl: true, queryParams: { id: this.requestId } };
        this.router.navigate(['/subscription'], params);
    }

    private initializeVideoStream(): void {
        this.navigationService.activity.loader.show('Инициализация камеры');

        if (navigator?.mediaDevices) {
            navigator.mediaDevices
                .getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
                .then((stream) => {
                    this.navigationService.activity.loader.hide();
                    this.backgroundVideoStream = stream;

                    const videoBackgroundElement = this.videoBackground?.nativeElement;
                    const streamTracks = this.backgroundVideoStream.getVideoTracks();

                    if (videoBackgroundElement) {
                        videoBackgroundElement.srcObject = this.backgroundVideoStream;
                        videoBackgroundElement.onloadedmetadata = () => {
                            videoBackgroundElement.play();
                        };
                    }

                    if (streamTracks.length === 1) {
                        this.scanCameraService.initialize('camera', streamTracks[0].getSettings().deviceId ?? '');
                        this.initializeScanEmitter();
                    }
                })
                .catch((error) => {
                    this.message = { text: error, type: 'warning' };
                    this.closePageWithError('Камера не найдена или занята другим приложением');
                });
        } else {
            this.permissionsError = true;
            this.closePageWithError('Камера недоступна. Возможно вы пользуетесь незащищенным соединением');
        }
    }

    private initializeScanEmitter(): void {
        this.scanCameraService.emitter$.pipe(takeUntil(this.dead$)).subscribe({
            next: (event) => {
                switch (event.type) {
                    case AppScanEventType.Initialized:
                        this.initialized$.next(true);
                        this.permissionsError = false;
                        break;
                    case AppScanEventType.Scanning:
                        this.message = { text: 'Сканирование', type: 'process' };
                        break;
                    case AppScanEventType.Success:
                        this.scanCameraService.pause(true);
                        const divsInCamElement = this.cameraOutput?.nativeElement.getElementsByTagName('div');

                        if (divsInCamElement) {
                            for (let i = 0; i < divsInCamElement.length; i++) {
                                if (divsInCamElement[i].textContent == 'Scanner paused') {
                                    divsInCamElement[i].remove();
                                    break;
                                }
                            }
                        }

                        if (event.value) {
                            const result = event.value?.replace('http://info.midpass.ru/?id=', '');
                            if (!result.match(/^\d{25}$/)) {
                                this.message = { text: 'Неверный формат номера заявления', type: 'warning' };
                                this.scanCameraService.resume();
                            } else if (this.requestId) {
                                this.processing = true;
                                this.subscriptionService.subscribeExistingRequestByUid(this.requestId, result).subscribe({
                                    next: (response) => {
                                        this.onRequestUidSetupComplete(response);
                                    },
                                    error: (error) => {
                                        this.onRequestUidSetupFailure(error);
                                    },
                                });
                            }
                        }

                        break;
                }
            },
        });
    }

    navigateBack(): void {
        this.navigationService.navigateBack();
    }

    private onRequestUidSetupComplete(result: SubscriptionCreationResult): void {
        if (result.success) {
            this.processing = false;
            this.navigationService.navigateBack();
        } else {
            this.onRequestUidSetupFailure(result.error);
        }
    }

    private onRequestUidSetupFailure(error: any) {
        if (error === 'REQUEST_NOT_ACCEPTED') {
            let message =
                'Уважаемый заявитель, информация о заявлении ' +
                'из консульского учреждения пока не поступила. ' +
                'Пожалуйста, повторите попытку позднее.';
            this.dialogService.notifyInMessageBox('Внимание', message).subscribe(() => this.navigationService.navigateBack());
            this.processing = false;
            return;
        }

        let message: string = 'Ошибка обработки данных';
        switch (error) {
            case 'REQUEST_NOT_FOUND':
                message = 'Данные о заявлении отсутствуют';
                break;
            case 'REQUEST_UID_MISSING':
                message = 'Отсутствует UID в запросе верификации';
                break;
            case 'APPLICANT_BIRTH_DATE_MISSING':
                message = 'Отсутствует дата рождения в запросе верификации';
                break;
            case 'APPLICANT_BIRTHDATE_MISMATCH':
                message = 'Считанный номер заявления принадлежит другому заявителю.';
                break;
        }

        this.message = { text: message, type: 'warning' };
        this.processing = false;
        this.scanCameraService.resume();
    }

    private closePageWithError(error?: string): void {
        this.navigationService.activity.loader.hide();
        this.dialogService.notifyInSnackBar(error ?? `Неизвестная ошибка: ${error}`, 'error');
        this.navigateBack();
    }
}
